import { hot } from 'react-hot-loader';

import React from 'react';
import PropTypes from 'prop-types';

import Component from './BaseComponent';

import './styles/ProgressBar.styl';
import { stringifyTime } from '../util/Time';
import { limit } from '../util/Math';

import _ from 'lodash';
import {DRAG_EVENTS} from '../constants/DragEvents';
import { ThemeContainer } from './ThemeContextProvider';

const $ = window.$;

/*
	PROGRESSBAR - SELF CONTAINED TO DISPLAY CURRENT PLAYBACK TIME RESPOND TO USER INTERACTION
*/


class ProgressBar extends Component {

	state = {
		//whilst dragging the fill has to follow the finger, not glide after it
		isDragging : false,
		//the time under the finger - drawn straight away, the player catches up
		dragTime : 0
	}

	onInteractionUpdate( evt ){
		//two fingers on the bar means the reader is zooming the page - let go of the drag
		if( evt.touches && evt.touches.length > 1 ){
			if( this.state.isDragging ){
				this.removeEventListeners();
				this.cancelSeek();
				this.setState({isDragging:false});
				this.props.onDragEnd();
			}
			return;
		}

		//a touch drag would otherwise scroll the page instead of moving the playhead
		if( evt.cancelable && _.startsWith( evt.type, 'touch' ) ){
			evt.preventDefault();
		}

		switch( evt.type ){
			case 'mousedown':
			case 'touchstart':
				this.addEventListeners();
				this.setState({isDragging:true,dragTime:this.updateDragProgress( evt )});
				this.props.onDragStart();
				this.requestSeek( this.updateDragProgress( evt ) );
			break;
			case 'mousemove':
			case 'touchmove':
				//get the position of the mouse relative to the position and size of the slider
				this.setState({dragTime:this.updateDragProgress( evt )});
				this.requestSeek( this.updateDragProgress( evt ) );
			break;
			case 'mouseup':
			case 'touchend':
				this.removeEventListeners();
				//seek to where the finger actually left off before handing back
				this.cancelSeek();
				this.props.onDragUpdate( this.state.dragTime );
				this.setState({isDragging:false});
				this.props.onDragEnd();
			break;
			default:
				window.log(`Unexpected mouse event ${evt.type}`);
			break;
		}
	}

	componentWillUnmount(){
		this.removeEventListeners();
		this.cancelSeek();
	}

	/*
		SEEKING REDRAWS THE WHOLE NOTE - ONE SEEK PER FRAME IS PLENTY,
		THE BAR ITSELF FOLLOWS THE FINGER FROM ITS OWN STATE
	*/
	requestSeek( time ){
		this.seekTime = time;

		if( !this.seekFrame ){
			this.seekFrame = window.requestAnimationFrame( () => {
				this.seekFrame = null;
				this.props.onDragUpdate( this.seekTime );
			} );
		}
	}

	cancelSeek(){
		if( this.seekFrame ){
			window.cancelAnimationFrame( this.seekFrame );
			this.seekFrame = null;
		}
	}

	onRefSlider( ref ){
		if( ref ){
			this.slider = ref;
			//react's own touch handlers are passive, so preventDefault would be ignored
			ref.addEventListener( 'touchstart', this.onInteractionUpdate, {passive:false} );
		}
	}

	isRightToLeft(){
		//the bar fills from the right when the page reads that way
		return this.slider && window.getComputedStyle( this.slider ).direction === 'rtl';
	}

	updateDragProgress( evt ){
		if( !_.isUndefined( this.slider ) ){
			const {duration} = this.props;
			const {pageX} = _.first( evt.touches ) || evt;

			const {left:sliderX} = $(this.slider).offset();
			const sliderWidth = $(this.slider).width();

			var x = pageX - sliderX;
			var progress = limit( x / sliderWidth, 0, 1 );

			if( this.isRightToLeft() ){
				progress = 1 - progress;
			}

			var currentTime = duration * progress;

			return currentTime;
		}

		return 0;
	}

	addEventListeners(){
		_.each( DRAG_EVENTS, ( eventName ) => {
			//passive:false so the touch move can be claimed from the page scroll
			window.addEventListener( eventName, this.onInteractionUpdate, {passive:false} );
		} );
	}
	
	removeEventListeners(){
		_.each( DRAG_EVENTS, ( eventName ) => {
			window.removeEventListener( eventName, this.onInteractionUpdate, {passive:false} );
		} );
	}

	render(){
		const {duration,events} = this.props;
		//whilst dragging we draw our own position - it doesn't wait on the player
		const currentTime = this.state.isDragging ? this.state.dragTime : this.props.currentTime;
		return <ThemeContainer className="ProgressBar" data-dragging={this.state.isDragging ? "true" : "false"}>
			{/* THUS IS THE BACKGROUND GREY BAR */}
			<div className="Slider" ref={ _.isUndefined( this.slider ) ? this.onRefSlider : null} onMouseDown={this.onInteractionUpdate}>
				{/* THESE ARE MARKERS TO INDICATE EVENTS ON THE TIMELINE */}
				{_.map(events, ( event, index ) => {
					//the markers run with the bar, so they start from the other end in rtl
					const position = Math.round(100*Math.min(1,event.duration/duration));
					//once the playhead is past a marker the played bar covers it
					const isPassed = _.isNumber( event.duration ) && event.duration <= currentTime;
					return <div key={index} className="Event" data-passed={isPassed ? "true" : "false"} style={ this.isRightToLeft() ? {right:`${position}%`} : {left:`${position}%`} } />
				})}
				{/* THIS IS THE PROGRESS BAR FILL */}
				{/* NOT ROUNDED - WHOLE PERCENTAGES STEP THE FILL ACROSS THE BAR */}
				<div className="Progress" style={{width:`${(100*Math.min(1,currentTime/duration)).toFixed(3)}%`}} />
			</div>
			{/* THIS DISPLAY THE AMOUNT OF TIME REMAINING */}
			{/* a clock reads left to right whichever way the page does - and the minus stays in front */}
			<div className="Time" dir="ltr">-{stringifyTime( duration - currentTime )}</div>
		</ThemeContainer>
	}
}

ProgressBar.propTypes = {
	onDragStart : PropTypes.func.isRequired,
	onDragUpdate : PropTypes.func.isRequired,
	onDragEnd : PropTypes.func.isRequired,
}

ProgressBar.defaultProps = {
	onDragStart : () => {},
	onDragUpdate : () => {},
	onDragEnd : () => {},
}

export default hot(module)(ProgressBar);
